import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import { DEFAULT_TOOL_SCOPES } from '../constants';
import McpSetup from '../components/McpSetup';
import { ExecutionGrantDurationField } from '../components/ExecutionGrantDurationField';
import './tokenSettings.css';
import type {
  AppItem,
  ExecGrant,
  ExecGrantCreateResponse,
  ExecGrantListResponse,
  Server,
  ToolApproval
} from '../types';
import { reportErrorLocal } from '../utils/errors';
import {
  EXEC_GRANT_DEFAULT_DURATION,
  parseExecutionGrantDuration,
  type ExecutionGrantDurationUnit
} from '../utils/executionGrantDuration';

interface ToolTokenResponse {
  id: string;
  name: string;
  scopes: string[];
  app_ids: string[];
  app_scope_mode: string;
  server_scope_mode: string;
  server_ids: string[];
  default_server_id: string | null;
  operation_policy: string;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
}

interface CreateTokenResponse {
  id: string;
  name: string;
  token: string;
  scopes: string[];
  app_ids: string[];
  app_scope_mode: string;
  server_scope_mode: string;
  server_ids: string[];
  default_server_id: string | null;
  operation_policy: string;
  mcp_url: string;
}

interface ScopeInfo {
  name: string;
  description: string;
}

interface AvailableScopesResponse {
  scopes: ScopeInfo[];
}

const EXPLICIT_ACTION_SCOPES = new Set([
  'pipelines:retry',
  'pipelines:play',
  'pipelines:play:*',
  'ui-validation:run',
  'tool-settings:write',
  'merge_requests:write',
  'conversations:create',
  'conversations:messages:write',
  'executors:view',
  'executors:operate',
  'executors:terminal',
  'executors:terminal:privileged',
  'executors:terminal-identities:manage',
  'hosts:maintain',
  'migrations:backup',
  'migrations:restore',
  'migrations:validate',
  'migrations:cutover',
  'migration-artifacts:delete',
  'registry-credentials:manage',
  'apps:create'
]);

const SELF_APP_OPERATION_SCOPES = new Set([
  'runtime-validation:read',
  'runtime-validation:run',
  'github-releases:run'
]);

type Props = {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
};

type TokenPagePrefill = {
  fromCli: boolean;
  name: string;
  scopes?: string[];
  appIds: string[];
  appScopeMode: string;
  serverIds: string[];
  serverScopeMode: string;
  defaultServerId: string;
  operationPolicy: string;
};

function splitParamList(value: string | null): string[] {
  return (value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function repeatedOrCsvParams(params: URLSearchParams, repeatedKey: string, csvKey: string): string[] {
  const repeated = params.getAll(repeatedKey).flatMap((value) => splitParamList(value));
  if (repeated.length > 0) {
    return Array.from(new Set(repeated));
  }
  return Array.from(new Set(splitParamList(params.get(csvKey))));
}

function normalizeDeployStageScope(value: string): string | null {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
  return normalized ? `pipelines:play:${normalized}` : null;
}

function appSelectionIncludesSelfApp(
  apps: AppItem[],
  appIds: string[],
  appScopeMode: string
): boolean {
  const selfAppIds = new Set(apps.filter((app) => app.is_self).map((app) => app.id));
  if (selfAppIds.size === 0) return false;
  if (appScopeMode === 'all_apps') return true;
  return appIds.some((appId) => selfAppIds.has(appId));
}

function readTokenPagePrefill(): TokenPagePrefill {
  const params = new URLSearchParams(window.location.search);
  const fromCli = params.get('source') === 'cli';
  const hasScopes = params.has('scope') || params.has('scopes');
  const scopes = hasScopes ? repeatedOrCsvParams(params, 'scope', 'scopes') : undefined;
  return {
    fromCli,
    name: params.get('name')?.trim() || (fromCli ? 'Local CLI' : ''),
    scopes,
    appIds: repeatedOrCsvParams(params, 'app_id', 'app_ids'),
    appScopeMode: params.get('app_scope_mode') || 'selected_apps',
    serverIds: repeatedOrCsvParams(params, 'server_id', 'server_ids'),
    serverScopeMode: params.get('server_scope_mode') || 'all_servers',
    defaultServerId: params.get('default_server_id') || '',
    operationPolicy: params.get('operation_policy') || 'standard'
  };
}

export default function TokensSettingsPage({ apiClient, setError }: Props) {
  const prefill = readTokenPagePrefill();
  const [tokens, setTokens] = useState<ToolTokenResponse[]>([]);
  const [apps, setApps] = useState<AppItem[]>([]);
  const [servers, setServers] = useState<Server[]>([]);
  const [availableScopes, setAvailableScopes] = useState<ScopeInfo[]>([]);
  const [approvalsByTokenId, setApprovalsByTokenId] = useState<Record<string, ToolApproval[]>>({});
  const [execGrantsByTokenId, setExecGrantsByTokenId] = useState<Record<string, ExecGrant[]>>({});
  const [execGrantKeysByTokenId, setExecGrantKeysByTokenId] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [approvalBusyKey, setApprovalBusyKey] = useState<string | null>(null);
  const [execGrantBusyKey, setExecGrantBusyKey] = useState<string | null>(null);
  const [execGrantFormTokenId, setExecGrantFormTokenId] = useState<string | null>(null);
  const [execGrantName, setExecGrantName] = useState('Local Codex command grant');
  const [execGrantDurationAmount, setExecGrantDurationAmount] = useState(EXEC_GRANT_DEFAULT_DURATION);
  const [execGrantDurationUnit, setExecGrantDurationUnit] = useState<ExecutionGrantDurationUnit>('minutes');
  const [execGrantPrivileged, setExecGrantPrivileged] = useState(false);
  const [execGrantExecutorRefs, setExecGrantExecutorRefs] = useState('');
  const [execGrantMaxUses, setExecGrantMaxUses] = useState('');

  // Form state
  const [showCreateForm, setShowCreateForm] = useState(prefill.fromCli);
  const [editingTokenId, setEditingTokenId] = useState<string | null>(null);
  const [newTokenName, setNewTokenName] = useState(prefill.name);
  const [newTokenScopes, setNewTokenScopes] = useState<string[]>(
    prefill.scopes ?? DEFAULT_TOOL_SCOPES
  );
  const [newTokenAppIds, setNewTokenAppIds] = useState<string[]>(prefill.appIds);
  const [newTokenAppScopeMode, setNewTokenAppScopeMode] = useState(prefill.appScopeMode);
  const [newTokenServerIds, setNewTokenServerIds] = useState<string[]>(prefill.serverIds);
  const [newTokenServerScopeMode, setNewTokenServerScopeMode] = useState(prefill.serverScopeMode);
  const [newTokenDefaultServerId, setNewTokenDefaultServerId] = useState(prefill.defaultServerId);
  const [newTokenOperationPolicy, setNewTokenOperationPolicy] = useState(prefill.operationPolicy);
  const [createdToken, setCreatedToken] = useState<CreateTokenResponse | null>(null);
  const hasSelfAppAccess = appSelectionIncludesSelfApp(
    apps,
    newTokenAppIds,
    newTokenAppScopeMode
  );

  function resetTokenForm() {
    setEditingTokenId(null);
    setNewTokenName('');
    setNewTokenScopes(DEFAULT_TOOL_SCOPES);
    setNewTokenAppIds([]);
    setNewTokenAppScopeMode('selected_apps');
    setNewTokenServerIds([]);
    setNewTokenServerScopeMode('all_servers');
    setNewTokenDefaultServerId('');
    setNewTokenOperationPolicy('standard');
    setShowCreateForm(false);
  }

  function resetExecGrantForm() {
    setExecGrantName('Local Codex command grant');
    setExecGrantDurationAmount(EXEC_GRANT_DEFAULT_DURATION);
    setExecGrantDurationUnit('minutes');
    setExecGrantPrivileged(false);
    setExecGrantExecutorRefs('');
    setExecGrantMaxUses('');
    setExecGrantFormTokenId(null);
  }

  function startCreateToken() {
    if (showCreateForm && !editingTokenId) {
      resetTokenForm();
      return;
    }
    setCreatedToken(null);
    setEditingTokenId(null);
    setNewTokenName(prefill.name);
    setNewTokenScopes(prefill.scopes ?? DEFAULT_TOOL_SCOPES);
    setNewTokenAppIds(prefill.appIds);
    setNewTokenAppScopeMode(prefill.appScopeMode);
    setNewTokenServerIds(prefill.serverIds);
    setNewTokenServerScopeMode(prefill.serverScopeMode);
    setNewTokenDefaultServerId(prefill.defaultServerId);
    setNewTokenOperationPolicy(prefill.operationPolicy);
    setShowCreateForm(true);
  }

  function startEditToken(token: ToolTokenResponse) {
    setCreatedToken(null);
    setStatusMessage(null);
    setEditingTokenId(token.id);
    setNewTokenName(token.name);
    setNewTokenScopes(token.scopes);
    setNewTokenAppIds(token.app_ids);
    setNewTokenAppScopeMode(token.app_scope_mode);
    setNewTokenServerIds(token.server_ids);
    setNewTokenServerScopeMode(token.server_scope_mode);
    setNewTokenDefaultServerId(token.default_server_id ?? '');
    setNewTokenOperationPolicy(token.operation_policy || 'standard');
    setShowCreateForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const loadTokens = useCallback(async () => {
    try {
      const data = await apiClient.get<ToolTokenResponse[]>('/api/tokens');
      setTokens(data);
    } catch (err) {
      reportErrorLocal('Failed to load tokens', err, setError, setLocalError);
    }
  }, [apiClient, setError]);

  const loadApprovals = useCallback(
    async (tokenIds: string[]) => {
      try {
        const entries = await Promise.all(
          tokenIds.map(async (tokenId) => {
            const approvals = await apiClient.get<ToolApproval[]>(
              `/api/tokens/${tokenId}/approvals?status=pending&limit=10`
            );
            return [tokenId, approvals] as const;
          })
        );
        setApprovalsByTokenId(Object.fromEntries(entries));
      } catch (err) {
        reportErrorLocal('Failed to load token approvals', err, setError, setLocalError);
      }
    },
    [apiClient, setError]
  );

  const loadExecGrants = useCallback(
    async (tokenIds: string[]) => {
      try {
        const entries = await Promise.all(
          tokenIds.map(async (tokenId) => {
            const response = await apiClient.get<ExecGrantListResponse>(
              `/api/tokens/${tokenId}/exec-grants`
            );
            return [tokenId, response.grants] as const;
          })
        );
        setExecGrantsByTokenId(Object.fromEntries(entries));
      } catch (err) {
        reportErrorLocal('Failed to load execution grants', err, setError, setLocalError);
      }
    },
    [apiClient, setError]
  );

  const loadApps = useCallback(async () => {
    try {
      const data = await apiClient.get<AppItem[]>('/api/apps');
      setApps(data);
    } catch (err) {
      reportErrorLocal('Failed to load apps', err, setError, setLocalError);
    }
  }, [apiClient, setError]);

  const loadServers = useCallback(async () => {
    try {
      const data = await apiClient.get<Server[]>('/api/servers');
      setServers(data);
    } catch (err) {
      reportErrorLocal('Failed to load servers', err, setError, setLocalError);
    }
  }, [apiClient, setError]);

  const loadScopes = useCallback(async () => {
    try {
      const data = await apiClient.get<AvailableScopesResponse>('/api/tokens/scopes');
      setAvailableScopes(data.scopes);
    } catch (err) {
      reportErrorLocal('Failed to load scopes', err, setError, setLocalError);
    }
  }, [apiClient, setError]);

  useEffect(() => {
    void Promise.all([loadTokens(), loadApps(), loadServers(), loadScopes()]);
  }, [loadApps, loadScopes, loadServers, loadTokens]);

  useEffect(() => {
    if (tokens.length === 0) {
      setApprovalsByTokenId({});
      setExecGrantsByTokenId({});
      return;
    }
    void loadApprovals(tokens.map((token) => token.id));
    void loadExecGrants(tokens.map((token) => token.id));
  }, [loadApprovals, loadExecGrants, tokens]);

  function toggleScope(scope: string) {
    setNewTokenScopes((prev) => {
      if (prev.includes(scope)) {
        return prev.filter((value) => value !== scope);
      }
      return [...prev, scope];
    });
  }

  function toggleApp(appId: string) {
    const nextAppIds = newTokenAppIds.includes(appId)
      ? newTokenAppIds.filter((value) => value !== appId)
      : [...newTokenAppIds, appId];
    setNewTokenAppIds(nextAppIds);
    if (!appSelectionIncludesSelfApp(apps, nextAppIds, newTokenAppScopeMode)) {
      setNewTokenScopes((prev) =>
        prev.filter((scope) => !SELF_APP_OPERATION_SCOPES.has(scope))
      );
    }
  }

  function updateAppScopeMode(appScopeMode: string) {
    setNewTokenAppScopeMode(appScopeMode);
    if (!appSelectionIncludesSelfApp(apps, newTokenAppIds, appScopeMode)) {
      setNewTokenScopes((prev) =>
        prev.filter((scope) => !SELF_APP_OPERATION_SCOPES.has(scope))
      );
    }
  }

  function toggleServer(serverId: string) {
    setNewTokenServerIds((prev) => {
      if (prev.includes(serverId)) {
        const next = prev.filter((value) => value !== serverId);
        if (newTokenDefaultServerId === serverId) {
          setNewTokenDefaultServerId(next[0] || '');
        }
        return next;
      }
      return [...prev, serverId];
    });
  }

  function updateStageDeployScopes(value: string) {
    const scopes = Array.from(
      new Set(
        value
          .split(',')
          .map(normalizeDeployStageScope)
          .filter((scope): scope is string => Boolean(scope))
      )
    );
    setNewTokenScopes((prev) => [
      ...prev.filter((scope) => !scope.startsWith('pipelines:play:') || scope === 'pipelines:play:*'),
      ...scopes
    ]);
  }

  async function saveToken(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy || !newTokenName.trim()) return;

    const payload = {
      name: newTokenName.trim(),
      scopes: hasSelfAppAccess
        ? newTokenScopes
        : newTokenScopes.filter((scope) => !SELF_APP_OPERATION_SCOPES.has(scope)),
      app_ids: newTokenAppIds,
      app_scope_mode: newTokenAppScopeMode,
      server_ids: newTokenServerIds,
      server_scope_mode: newTokenServerScopeMode,
      default_server_id: newTokenDefaultServerId || null,
      operation_policy: newTokenOperationPolicy,
    };

    setBusy(true);
    setCreatedToken(null);
    setStatusMessage(null);
    try {
      if (editingTokenId) {
        await apiClient.put<ToolTokenResponse>(`/api/tokens/${editingTokenId}`, payload);
        setStatusMessage('Token access updated.');
        resetTokenForm();
      } else {
        const data = await apiClient.post<CreateTokenResponse>('/api/tokens', payload);
        setCreatedToken(data);
        resetTokenForm();
      }
      await loadTokens();
    } catch (err) {
      reportErrorLocal(
        editingTokenId ? 'Failed to update token access' : 'Failed to create token',
        err,
        setError,
        setLocalError
      );
    } finally {
      setBusy(false);
    }
  }

  async function revokeToken(tokenId: string) {
    if (busy) return;
    if (!window.confirm('Are you sure you want to revoke this token?')) return;

    setBusy(true);
    try {
      await apiClient.del(`/api/tokens/${tokenId}`);
      await loadTokens();
    } catch (err) {
      reportErrorLocal('Failed to revoke token', err, setError, setLocalError);
    } finally {
      setBusy(false);
    }
  }

  async function copyText(label: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied((current) => (current === label ? null : current)), 1500);
    } catch (err) {
      reportErrorLocal(`Failed to copy ${label}`, err, setError, setLocalError);
    }
  }

  async function respondToApproval(
    tokenId: string,
    approvalId: string,
    payload: { decision: 'approve' | 'deny'; remember?: boolean }
  ) {
    const busyKey = `${tokenId}:${approvalId}`;
    if (approvalBusyKey === busyKey) {
      return;
    }
    setApprovalBusyKey(busyKey);
    try {
      await apiClient.post(`/api/tokens/${tokenId}/approvals/${approvalId}/respond`, payload);
      await loadApprovals([tokenId]);
    } catch (err) {
      reportErrorLocal('Failed to respond to token approval', err, setError, setLocalError);
    } finally {
      setApprovalBusyKey((current) => (current === busyKey ? null : current));
    }
  }

  async function createExecGrant(tokenId: string) {
    const busyKey = `${tokenId}:create`;
    if (execGrantBusyKey === busyKey) return;
    const duration = parseExecutionGrantDuration(execGrantDurationAmount, execGrantDurationUnit);
    if (duration.minutes === null) {
      setLocalError(duration.error);
      return;
    }
    setExecGrantBusyKey(busyKey);
    setStatusMessage(null);
    try {
      const executorRefs = execGrantExecutorRefs
        .split(/[\s,]+/)
        .map((value) => value.trim())
        .filter(Boolean);
      const response = await apiClient.post<ExecGrantCreateResponse>(
        `/api/tokens/${tokenId}/exec-grants`,
        {
          name: execGrantName.trim() || undefined,
          ttl_minutes: duration.minutes,
          modes: execGrantPrivileged ? ['user', 'privileged'] : ['user'],
          executor_refs: executorRefs,
          max_uses: execGrantMaxUses.trim() ? Number(execGrantMaxUses) : undefined
        }
      );
      setExecGrantKeysByTokenId((prev) => ({ ...prev, [tokenId]: response.key }));
      await loadExecGrants([tokenId]);
      setStatusMessage('Execution grant created. Copy the grant key now; it will not be shown again.');
      resetExecGrantForm();
    } catch (err) {
      reportErrorLocal('Failed to create execution grant', err, setError, setLocalError);
    } finally {
      setExecGrantBusyKey((current) => (current === busyKey ? null : current));
    }
  }

  async function revokeExecGrant(tokenId: string, grantId: string) {
    const busyKey = `${tokenId}:${grantId}`;
    if (execGrantBusyKey === busyKey) return;
    setExecGrantBusyKey(busyKey);
    try {
      await apiClient.del(`/api/tokens/${tokenId}/exec-grants/${grantId}`);
      await loadExecGrants([tokenId]);
    } catch (err) {
      reportErrorLocal('Failed to revoke execution grant', err, setError, setLocalError);
    } finally {
      setExecGrantBusyKey((current) => (current === busyKey ? null : current));
    }
  }

  const getMcpUrl = useCallback(() => {
    // Extract from any token's mcp_url or construct from window.location
    if (createdToken?.mcp_url) {
      return createdToken.mcp_url;
    }
    const origin = window.location.origin;
    return `${origin}/api/mcp`;
  }, [createdToken]);

  const mcpUrl = getMcpUrl();
  const apiBase = window.location.origin;

  const shellQuote = (value: string) => `'${value.replace(/'/g, `'"'"'`)}'`;
  const selectedMcpLoginArgs = [
    newTokenName.trim() ? `--mcp-token-name ${shellQuote(newTokenName.trim())}` : null,
    ...newTokenScopes.map((scope) => `--mcp-scope ${shellQuote(scope)}`),
    newTokenScopes.length === 0 ? '--no-mcp-scopes' : null,
    newTokenAppScopeMode !== 'selected_apps'
      ? `--mcp-app-scope-mode ${shellQuote(newTokenAppScopeMode)}`
      : null,
    ...newTokenAppIds.map((appId) => `--mcp-app ${shellQuote(appId)}`),
    newTokenServerScopeMode !== 'all_servers'
      ? `--mcp-server-scope-mode ${shellQuote(newTokenServerScopeMode)}`
      : null,
    ...newTokenServerIds.map((serverId) => `--mcp-server ${shellQuote(serverId)}`),
    newTokenDefaultServerId ? `--mcp-default-server ${shellQuote(newTokenDefaultServerId)}` : null,
    newTokenOperationPolicy !== 'standard'
      ? `--mcp-operation-policy ${shellQuote(newTokenOperationPolicy)}`
      : null
  ].filter(Boolean);
  const cliLoginCommand = `mission-control admin login --api-base ${shellQuote(apiBase)}${
    selectedMcpLoginArgs.length ? ` ${selectedMcpLoginArgs.join(' ')}` : ''
  }`;
  const getAppName = (appId: string) => {
    const app = apps.find((a) => a.id === appId);
    return app?.name || appId.slice(0, 8);
  };

  const getServerName = (serverId: string) => {
    const server = servers.find((item) => item.id === serverId);
    return server?.name || serverId.slice(0, 8);
  };
  const generalScopes = availableScopes.filter(
    (scope) => !SELF_APP_OPERATION_SCOPES.has(scope.name)
  );
  const readScopes = generalScopes.filter((scope) => !EXPLICIT_ACTION_SCOPES.has(scope.name));
  const actionScopes = generalScopes.filter((scope) => EXPLICIT_ACTION_SCOPES.has(scope.name));
  const selfAppScopes = availableScopes.filter((scope) =>
    SELF_APP_OPERATION_SCOPES.has(scope.name)
  );
  const stageDeployScopeText = newTokenScopes
    .filter((scope) => scope.startsWith('pipelines:play:') && scope !== 'pipelines:play:*')
    .map((scope) => scope.slice('pipelines:play:'.length))
    .join(', ');
  const renderScopeCheckbox = (scope: ScopeInfo) => (
    <label key={scope.name} className="checkbox-row">
      <input
        type="checkbox"
        checked={newTokenScopes.includes(scope.name)}
        onChange={() => toggleScope(scope.name)}
      />
      <span>
        {scope.description} <span className="status">({scope.name})</span>
      </span>
    </label>
  );

  const renderExecGrantPanel = (tokenId: string, revoked: boolean) => {
    const grants = execGrantsByTokenId[tokenId] || [];
    const grantKey = execGrantKeysByTokenId[tokenId];
    const grantInstruction = grantKey
      ? `For Mission Control MCP ops.exec calls, pass grant_key: ${grantKey}`
      : '';
    const formOpen = execGrantFormTokenId === tokenId;
    return (
      <div className="stack" style={{ marginTop: '0.75rem' }}>
        <div className="row">
          <div>
            <strong>Execution grants</strong>
            <div className="status">
              Temporary keys for local MCP clients. They bypass command approval only within this
              token's existing executor access.
            </div>
          </div>
          <button
            type="button"
            className="secondary"
            disabled={revoked}
            onClick={() => {
              if (formOpen) {
                resetExecGrantForm();
              } else {
                resetExecGrantForm();
                setExecGrantFormTokenId(tokenId);
              }
            }}
          >
            {formOpen ? 'Cancel grant' : 'Create grant'}
          </button>
        </div>

        {grantKey && (
          <div className="token-created">
            <div>
              <h3>Execution grant created</h3>
              <p className="status">Copy this grant key now. It will not be shown again.</p>
            </div>
            <div className="token-command token-command--secret">
              <code>{grantKey}</code>
              <button
                type="button"
                className="ghost"
                onClick={() => void copyText(`exec grant ${tokenId}`, grantKey)}
              >
                {copied === `exec grant ${tokenId}` ? 'Copied' : 'Copy grant key'}
              </button>
            </div>
            <div className="token-command">
              <code>{grantInstruction}</code>
              <button
                type="button"
                className="ghost"
                onClick={() => void copyText(`exec grant instructions ${tokenId}`, grantInstruction)}
              >
                {copied === `exec grant instructions ${tokenId}` ? 'Copied' : 'Copy instruction'}
              </button>
            </div>
          </div>
        )}

        {formOpen && (
          <div className="stack" style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', padding: '0.75rem' }}>
            <div className="form-grid">
              <label>
                <span className="status">Grant name</span>
                <input
                  type="text"
                  value={execGrantName}
                  onChange={(e) => setExecGrantName(e.target.value)}
                  maxLength={120}
                />
              </label>
              <label>
                <span className="status">Max commands</span>
                <input
                  type="number"
                  min={1}
                  value={execGrantMaxUses}
                  onChange={(e) => setExecGrantMaxUses(e.target.value)}
                  placeholder="Unlimited"
                />
              </label>
            </div>
            <ExecutionGrantDurationField
              amount={execGrantDurationAmount}
              unit={execGrantDurationUnit}
              onAmountChange={setExecGrantDurationAmount}
              onUnitChange={setExecGrantDurationUnit}
              disabled={execGrantBusyKey === `${tokenId}:create`}
            />
            <label>
              <span className="status">Executor ids</span>
              <input
                type="text"
                value={execGrantExecutorRefs}
                onChange={(e) => setExecGrantExecutorRefs(e.target.value)}
                placeholder="Optional: executor ids separated by commas or spaces"
              />
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={execGrantPrivileged}
                onChange={(e) => setExecGrantPrivileged(e.target.checked)}
              />
              <span>Allow privileged mode</span>
            </label>
            <div className="row">
              <button
                type="button"
                disabled={execGrantBusyKey === `${tokenId}:create` ||
                  parseExecutionGrantDuration(execGrantDurationAmount, execGrantDurationUnit).minutes === null}
                onClick={() => void createExecGrant(tokenId)}
              >
                {execGrantBusyKey === `${tokenId}:create` ? 'Creating...' : 'Create execution grant'}
              </button>
              <button type="button" className="secondary" onClick={resetExecGrantForm}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {grants.length > 0 ? (
          <div className="stack">
            {grants.map((grant) => (
              <div
                key={grant.id}
                className="stack"
                style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', padding: '0.75rem' }}
              >
                <div className="row">
                  <strong>{grant.name}</strong>
                  <span className={grant.active ? 'status success' : 'status'}>
                    {grant.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="status">
                  Modes: {grant.modes.join(', ')} | Executors:{' '}
                  {grant.executor_scope === 'specific' ? grant.executor_refs.join(', ') : 'All visible'}
                </div>
                <div className="status">
                  Uses: {grant.use_count}
                  {grant.max_uses ? ` / ${grant.max_uses}` : ''} | Expires:{' '}
                  {new Date(grant.expires_at).toLocaleString()}
                </div>
                {grant.last_used_at && (
                  <div className="status">Last used: {new Date(grant.last_used_at).toLocaleString()}</div>
                )}
                {grant.revoked_at ? (
                  <div className="status">Revoked: {new Date(grant.revoked_at).toLocaleString()}</div>
                ) : (
                  <button
                    type="button"
                    className="secondary"
                    disabled={execGrantBusyKey === `${tokenId}:${grant.id}`}
                    onClick={() => void revokeExecGrant(tokenId, grant.id)}
                  >
                    Revoke grant
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="status">No execution grants for this token.</div>
        )}
      </div>
    );
  };

  return (
    <div className="stack token-settings">
      <div className="card token-settings__hero">
        <div className="card-header">
          <div>
            <h2>API Tokens</h2>
            <p className="status">
              Use these tokens for the local CLI, automation, and optional MCP tool access. The CLI
              accepts the same token as a Bearer API token.
            </p>
          </div>
          <button
            type="button"
            className="secondary"
            onClick={startCreateToken}
            disabled={busy}
          >
            {showCreateForm && !editingTokenId ? 'Cancel' : 'Create API Token'}
          </button>
        </div>

        <McpSetup endpoint={mcpUrl} trustedApiOrigin={apiBase} cliCommand={cliLoginCommand} copied={copied} copy={copyText} />

        {localError && (
          <div className="status error-inline">
            {localError}
          </div>
        )}

        {statusMessage && (
          <div className="status">
            {statusMessage}
          </div>
        )}

        {createdToken && (
          <div className="token-created">
            <div>
              <h3>API token created</h3>
              <p className="status">Save this token now. It will not be shown again.</p>
            </div>

            <div className="token-command token-command--secret">
              <code>{createdToken.token}</code>
              <button type="button" className="ghost" onClick={() => void copyText('token', createdToken.token)}>
                {copied === 'token' ? 'Copied' : 'Copy token'}
              </button>
            </div>

            <p className="status">Use the client setup above. Paste this token only into the CLI prompt or your local secret environment—not into a chat or a command saved in shell history.</p>

            {renderExecGrantPanel(createdToken.id, false)}
          </div>
        )}

        {showCreateForm && (
          <form onSubmit={saveToken} className="stack token-create-form">
            <div>
              <h3>{editingTokenId ? 'Edit token access' : 'Create API token'}</h3>
              {editingTokenId && (
                <p className="status">
                  Update the stored access grants for this token. The token secret stays the same.
                </p>
              )}
            </div>
            <div>
              <label>
                <span className="status">Token name</span>
                <input
                  type="text"
                  value={newTokenName}
                  onChange={(e) => setNewTokenName(e.target.value)}
                  placeholder="Local CLI"
                  required
                  maxLength={100}
                />
              </label>
            </div>

            <div>
              <span className="status">Read scopes</span>
              <div className="token-scope-grid">
                {readScopes.map(renderScopeCheckbox)}
              </div>
            </div>

            <div>
              <span className="status">Explicit action grants</span>
              <p className="status" style={{ marginTop: '0.35rem' }}>
                Mutating actions are never selected by default. Deployment actions such as playing
                manual pipeline jobs are still restricted by the app access mode below.
              </p>
              <div className="token-scope-grid">
                {actionScopes.map(renderScopeCheckbox)}
              </div>
              <label style={{ marginTop: '0.75rem' }}>
                <span className="status">Stage deploy scopes</span>
                <input
                  type="text"
                  value={stageDeployScopeText}
                  onChange={(e) => updateStageDeployScopes(e.target.value)}
                  placeholder="dev, production"
                />
              </label>
              <p className="status" style={{ marginTop: '0.35rem' }}>
                Comma-separated GitLab environment or app stage names. These become
                pipelines:play:&lt;stage&gt; scopes for the stage-aware deploy tool.
              </p>
            </div>

            <div>
              <span className="status">Apps (optional - can be added later)</span>
              <p className="status" style={{ marginTop: '0.35rem' }}>
                App selections scope app-bound tools and data. Admin tokens also inherit command catalogs
                from apps marked as infrastructure helpers.
              </p>
              <label>
                <span className="status">App access mode</span>
                <select
                  value={newTokenAppScopeMode}
                  onChange={(e) => updateAppScopeMode(e.target.value)}
                >
                  <option value="selected_apps">Selected apps only</option>
                  <option value="all_apps">All apps I can access</option>
                </select>
              </label>
              <div className="token-app-grid">
                {apps.map((app) => (
                  <label key={app.id} className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={newTokenAppIds.includes(app.id)}
                      onChange={() => toggleApp(app.id)}
                    />
                    <span>{app.name} <span className="status">({app.slug})</span></span>
                  </label>
                ))}
              </div>
            </div>

            {hasSelfAppAccess && selfAppScopes.length > 0 && (
              <div>
                <span className="status">Self-app operations</span>
                <div className="token-scope-grid">
                  {selfAppScopes.map(renderScopeCheckbox)}
                </div>
              </div>
            )}

            <div>
              <span className="status">Servers</span>
              <p className="status" style={{ marginTop: '0.35rem' }}>
                Limit infrastructure and terminal tools to selected servers when the token should not
                see every server available to the user.
              </p>
              <label>
                <span className="status">Server access mode</span>
                <select
                  value={newTokenServerScopeMode}
                  onChange={(e) => setNewTokenServerScopeMode(e.target.value)}
                >
                  <option value="all_servers">All servers in scope</option>
                  <option value="selected_servers">Selected servers only</option>
                </select>
              </label>
              {newTokenServerScopeMode === 'selected_servers' && (
                <div className="token-app-grid">
                  {servers.map((server) => (
                    <label key={server.id} className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={newTokenServerIds.includes(server.id)}
                        onChange={() => toggleServer(server.id)}
                      />
                      <span>{server.name} <span className="status">({server.host})</span></span>
                    </label>
                  ))}
                  {servers.length === 0 && <p className="status">No servers configured.</p>}
                </div>
              )}
              {newTokenServerIds.length > 0 && (
                <label>
                  <span className="status">Default server</span>
                  <select
                    value={newTokenDefaultServerId}
                    onChange={(e) => setNewTokenDefaultServerId(e.target.value)}
                  >
                    <option value="">No default</option>
                    {newTokenServerIds.map((serverId) => {
                      const server = servers.find((item) => item.id === serverId);
                      return (
                        <option key={serverId} value={serverId}>
                          {server?.name || serverId}
                        </option>
                      );
                    })}
                  </select>
                </label>
              )}
            </div>

            <label>
              <span className="status">Operation policy</span>
              <input
                type="text"
                value={newTokenOperationPolicy}
                onChange={(e) => setNewTokenOperationPolicy(e.target.value)}
                placeholder="standard"
              />
            </label>

            <div className="row">
              <button type="submit" disabled={busy || !newTokenName.trim()}>
                {busy ? 'Saving...' : editingTokenId ? 'Save Access' : 'Create API Token'}
              </button>
              <button type="button" className="secondary" onClick={resetTokenForm} disabled={busy}>
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="stack token-list">
          {tokens.length === 0 ? (
            <p className="status">No tokens created yet.</p>
          ) : (
            tokens.map((token) => (
              <div key={token.id} className="card token-card">
                <div className="card-header">
                  <h3>{token.name}</h3>
                  <div className="row">
                    <Link to={`/settings/tokens/${token.id}`} className="link-button secondary">
                      Details
                    </Link>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => startEditToken(token)}
                      disabled={busy || token.revoked_at !== null}
                    >
                      Edit access
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => void revokeToken(token.id)}
                      disabled={busy || token.revoked_at !== null}
                    >
                      Revoke
                    </button>
                  </div>
                </div>
                <div className="stack">
                  <div className="token-meta-row">
                    <span className="mono">Status: {token.revoked_at ? 'Revoked' : 'Active'}</span>
                    <span className="mono">Created: {new Date(token.created_at).toLocaleString()}</span>
                  </div>
                  {token.last_used_at && (
                    <div className="status">Last used: {new Date(token.last_used_at).toLocaleString()}</div>
                  )}
                  <div className="status">Scopes: {token.scopes.join(', ')}</div>
                  <div className="status">
                    Apps: {token.app_ids.length > 0
                      ? token.app_ids.map(getAppName).join(', ')
                      : token.app_scope_mode === 'all_apps'
                        ? 'All accessible apps'
                        : 'None assigned'}
                  </div>
                  <div className="status">
                    Servers: {token.server_scope_mode === 'all_servers'
                      ? 'All servers in scope'
                      : token.server_ids.length > 0
                        ? token.server_ids.map(getServerName).join(', ')
                        : 'No servers selected'}
                  </div>
                  <div className="status">
                    Operation policy: {token.operation_policy || 'standard'}
                  </div>
                  {approvalsByTokenId[token.id]?.length ? (
                    <div className="stack" style={{ marginTop: '0.75rem' }}>
                      <div className="status">Pending approvals</div>
                      {approvalsByTokenId[token.id].map((approval) => {
                        const busyKey = `${token.id}:${approval.id}`;
                        return (
                          <div
                            key={approval.id}
                            className="stack"
                            style={{
                              border: '1px solid var(--border-color)',
                              borderRadius: '0.5rem',
                              padding: '0.75rem'
                            }}
                          >
                            <div className="row">
                              <strong>{approval.title}</strong>
                              <span className="status">{approval.server.name}</span>
                            </div>
                            {approval.detail && <div className="status">{approval.detail}</div>}
                            {approval.cmd && <code>{approval.cmd}</code>}
                            {approval.path && <code>{approval.path}</code>}
                            {approval.preview && (
                              <textarea className="log-box" rows={8} readOnly value={approval.preview} />
                            )}
                            <div className="row">
                              <button
                                type="button"
                                disabled={approvalBusyKey === busyKey}
                                onClick={() => void respondToApproval(token.id, approval.id, { decision: 'approve' })}
                              >
                                Approve
                              </button>
                              {approval.approval_kind === 'ops_exec' && (
                                <button
                                  type="button"
                                  disabled={approvalBusyKey === busyKey}
                                  onClick={() =>
                                    void respondToApproval(token.id, approval.id, {
                                      decision: 'approve',
                                      remember: true
                                    })
                                  }
                                >
                                  Approve & Remember
                                </button>
                              )}
                              <button
                                type="button"
                                className="secondary"
                                disabled={approvalBusyKey === busyKey}
                                onClick={() => void respondToApproval(token.id, approval.id, { decision: 'deny' })}
                              >
                                Reject
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  {renderExecGrantPanel(token.id, token.revoked_at !== null)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
